import React from 'react';
import PropTypes from 'prop-types';
import { Pagination as BootstrapPagination, Col, Container, Row } from 'react-bootstrap';
import Select from '../../../../modules/react/components/stateful/Select';
import styles from './styles.scss';

const PAGE_DISPLAY_COUNT = 10;

const perPageOptions = [
  { label: '5', value: 5 },
  { label: '10', value: 10 },
  { label: '25', value: 25 },
  { label: '50', value: 50 },
];

const propTypes = {
  disablePagination: PropTypes.bool,
  currentPageNumber: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onClick: PropTypes.func.isRequired,
  containerClassName: PropTypes.string,
  onChangePerPage: PropTypes.func.isRequired,
  descriptor: PropTypes.string.isRequired,
  totalItems: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,
  itemsPerPage: PropTypes.number.isRequired,
};

const defaultProps = {
  disablePagination: undefined,
  containerClassName: undefined,
};

const Pagination = ({
  disablePagination,
  currentPageNumber,
  totalPages,
  onClick,
  containerClassName,
  onChangePerPage,
  descriptor,
  totalItems,
  itemsPerPage,
}) => {
  const calcPagination = () => {
    let pageStart = currentPageNumber - (PAGE_DISPLAY_COUNT / 2);
    pageStart = pageStart > 0 ? pageStart : 1;

    let pageDisplayCount = totalPages > PAGE_DISPLAY_COUNT ? PAGE_DISPLAY_COUNT : totalPages;

    if (pageStart + pageDisplayCount > totalPages) {
      pageStart = (totalPages + 1) - pageDisplayCount;
      pageDisplayCount = (totalPages + 1) - pageStart;
    }

    return { pageStart, pageDisplayCount };
  };

  const createPages = () => {
    const { pageDisplayCount, pageStart } = calcPagination();
    const end = pageDisplayCount + pageStart;
    const pages = [];

    const createPage = (pageNumber) => {
      if (pageNumber === end) { return pages; }
      const active = pageNumber === currentPageNumber;

      let pageProps = {
        active,
        key: pageNumber,
        className: styles.paginationItem,
      };

      if (!active) { pageProps = { ...pageProps, onClick: () => onClick(pageNumber) }; }

      pages.push(<BootstrapPagination.Item {...pageProps}>{pageNumber}</BootstrapPagination.Item>);
      return createPage(pageNumber + 1);
    };
    return createPage(pageStart);
  };

  const changePerPage = (e) => {
    const perPage = e.value;
    const firstItem = ((currentPageNumber - 1) * itemsPerPage) + 1;

    const newPage = Math.ceil(firstItem / perPage);

    onChangePerPage({ newPage, perPage });
  };

  const displayRange = () => {
    const start = ((currentPageNumber - 1) * itemsPerPage) + 1;
    const end = currentPageNumber === totalPages ? totalItems : (start + itemsPerPage) - 1;

    return `${start} - ${end} of ${totalItems} ${descriptor}`;
  };

  const isFirst = currentPageNumber === 1;
  const isLast = currentPageNumber === totalPages;
  const disabled = { disabled: true };

  const firstProps = isFirst ? disabled : { onClick: () => onClick(1) };

  const prevProps = isFirst ? disabled : { onClick: () => onClick(currentPageNumber - 1) };

  const lastProps = isLast ? disabled : { onClick: () => onClick(totalPages) };

  const nextProps = isLast ? disabled : { onClick: () => onClick(currentPageNumber + 1) };

  const selectProps = {
    autoBlur: true,
    clearable: false,
    onChange: changePerPage,
    options: perPageOptions,
    value: itemsPerPage,
    searchable: false,
    className: styles.perPageSelect,
    menuPlacement: 'auto',
  };

  return (
    <Container className={containerClassName} data-testid="pagination">
      <Row>
        {!disablePagination && (
          <Col xs={12} sm={6} className={styles.wrapper}>
            <div className={styles.wrapper}>
              <BootstrapPagination>
                <BootstrapPagination.First {...firstProps} />
                <BootstrapPagination.Prev {...prevProps} />
                {createPages()}
                <BootstrapPagination.Next {...nextProps} />
                <BootstrapPagination.Last {...lastProps} />
              </BootstrapPagination>
            </div>
          </Col>
        )}
        <Col xs={12} sm={disablePagination ? 12 : 6} className={styles.itemInfo}>
          <span className={styles.perPage}>
            <span className={styles.itemsPerPage}>
              Items per page
            </span>
            <Select {...selectProps} />
          </span>
          <span className={styles.displayRange}>
            {displayRange()}
          </span>
        </Col>
      </Row>
    </Container>
  );
};

Pagination.propTypes = propTypes;

Pagination.defaultProps = defaultProps;

export default Pagination;
